import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Heart, Activity, Shield, Sparkles, Clock, Beaker, Award } from 'lucide-react';

export default function Benefits() {
  const benefits = [
    {
      icon: Activity,
      title: 'Made for an Active Life',
      description: 'A simple daily supplement for people who want to keep moving and keep doing the things they love.',
    },
    {
      icon: Clock,
      title: 'Built for a Daily Routine',
      description: 'Just two vegetarian capsules a day — easy to build into your morning or evening with food.',
    },
    {
      icon: Beaker,
      title: 'High-Bioavailability Curcumin',
      description: 'BCM-95® is absorbed better than standard curcumin, shown in published pharmacokinetic research.',
    },
    {
      icon: Award,
      title: 'One of the Most Researched',
      description: 'Over 75 published studies explore the BCM-95® extract\u2019s bioavailability and tolerability.',
    },
    {
      icon: Sparkles,
      title: 'Single-Plant & Clean',
      description: '100% turmeric extract with its own natural oils — piperine-free, allergen-free and vegan.',
    },
    {
      icon: Shield,
      title: 'Made to GMP Standards',
      description: 'Produced under GMP-certified manufacturing, in an amber-glass bottle to protect the extract.',
    },
  ];

  const sciencePoints = [
    {
      icon: Clock,
      title: 'Up to 8 Hours in the Body',
      description: 'Published research indicates BCM-95® stays measurable in the bloodstream for up to 8 hours, giving steady levels through the day.',
    },
    {
      icon: Beaker,
      title: '75+ Published Studies',
      description: 'The BCM-95® extract is the subject of extensive published research across multiple areas of study.',
    },
    {
      icon: Award,
      title: 'Extensively Studied Formulation',
      description: 'One of the most extensively researched curcumin formulations available today.',
    },
  ];

  return (
    <div className="min-h-screen">
      <section className="gradient-hero py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium tracking-widest uppercase text-primary mb-4 block">
              Clinically Researched Curcumin
            </span>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 font-heading" data-testid="benefits-page-title">
            Clinically Researched Curcumin for an Active Life
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              See why BCM-95® is one of the world&apos;s most extensively researched curcumin formulations — with over 75 published studies behind the extract — and why people who want to stay active make it part of their daily routine.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading" data-testid="health-benefits-title">
              Everyday Support for an Active Life
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              How BCM-95® fits into the lives of people who want to keep moving and keep doing what they enjoy.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-white to-primary/5 rounded-3xl p-8 shadow-soft hover:shadow-hover transition-all duration-500 border border-border/50"
                data-testid={`benefit-detail-${index}`}
              >
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                  <benefit.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3 font-heading">{benefit.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 gradient-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-widest uppercase text-primary mb-4 block">
              The Evidence
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading" data-testid="science-title">
              Proven by Science
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The research behind why BCM-95® is one of the most extensively studied curcumin formulations available.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {sciencePoints.map((point, index) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-3xl p-8 shadow-soft text-center"
                data-testid={`science-point-${index}`}
              >
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <point.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-3 font-heading">{point.title}</h3>
                <p className="text-muted-foreground">{point.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-12 shadow-soft"
          >
            <h3 className="text-3xl font-bold mb-6 font-heading text-center">What Makes BCM-95® Different?</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-xl font-semibold mb-4 text-primary">Superior Bioavailability</h4>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  BCM-95® combines 95% curcumin with turmeric essential oil enriched to 45% Ar-turmerone. Published pharmacokinetic research indicates up to 7 times better absorption than standard curcumin extracts.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Studies indicate free curcumin from BCM-95® remains measurable in the bloodstream for up to 8 hours, giving steady levels through the day.
                </p>
              </div>
              <div>
                <h4 className="text-xl font-semibold mb-4 text-primary">Published Research</h4>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  The BCM-95® extract has been the subject of published scientific research exploring areas such as:
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Curcumin bioavailability and absorption</li>
                  <li>• Tolerability over multi-week use</li>
                  <li>• Joint-comfort and active-living research populations</li>
                  <li>• Cognitive and mood research contexts</li>
                </ul>
                <p className="text-[12px] text-muted-foreground/70 italic mt-4 leading-relaxed">
                  These describe published research focus areas for the BCM-95® extract — not claims that BCM-95® treats, cures or prevents any condition.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-secondary text-white rounded-3xl p-12 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 font-heading">
              Stay Active. Stay Independent. Choose Science You Can Trust.
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Make BCM-95® part of your daily routine — a clinically researched curcumin from a brand that shows its proof.
            </p>
            <button
              onClick={() => window.location.href = '/products'}
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-10 py-5 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              data-testid="benefits-cta-button"
            >
              Add BCM-95® to Your Routine
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}